/**
 * Раскрытая строка ростера HR (03.10, владелец): сетка в 2 колонки по канону плотности —
 * Телефон (тап — чат в Telegram по номеру, карандаш — PhonePad M.3-Т), Нанят, Telegram
 * («привязан» или «Ссылка» — одноразовое приглашение в бота), Статус (переключатель
 * «работает / отпуск»). У ИО — чипы ролей. «Уволить» — только тем, кем этот человек
 * управляет (ОО — операторами), и никогда себе. Права решает сервер; здесь кнопки
 * просто не показываем тем, кому сервер всё равно откажет. Неполный телефон не сохраняется —
 * и об этом сказано под сеткой, а не молча (ревью 03.10).
 * 03.10 (владелец: «продолжи»): имя, ник и дата найма правятся карандашом — права как у статуса
 * (rights.manage: ОО — операторам, ИО — всем). Имя и ник — TextPad, дата — BirthPicker
 * (HIRED_MIN…сегодня, без «Без даты»; 13.10 ПР8 архива 1С — с 2019, одна константа с сервером); пустое имя,
 * будущая дата и дата раньше HIRED_MIN не уходят — причина под сеткой.
 * 16.10 (владелец): «Расстановка» — «не показывать в расстановке» для служебных аккаунтов: ставят ОО и EO любому,
 * после чего человека нет в дропдаунах доски ОО.
 * 27.10 (владелец: «пускай видят всю историю, если им выдал разрешение EO или OO»): «История клиентов» — флаг
 * history_all, ставят ОО и EO; ДК, ОО и EO и так видят всю историю (по роли) — им переключатель не нужен.
 */
import { useState } from "react";
import { PhonePad, Switch, TextPad, actionPairCss as ap } from "../../components/shared";
import { BirthPicker } from "../../components/shared/BirthPicker";
import { fmtDay, phoneState, type Invite, type StaffRow } from "../../hooks/staffApi";
import type { ProfilePatch } from "../../hooks/useStaffAdmin";
import type { StaffRoleId } from "@ub/shared/staffRoles";
import { HIRED_MIN, HIRED_MIN_YEAR } from "@ub/shared/archive1c";
import { openTgChatByPhone } from "../../lib/telegram";
import { RoleChips } from "./RoleChips";
import { InviteCard } from "./InviteCard";
import { AccessLogSheet } from "./AccessLogSheet";
import css from "./roster.module.css";

export interface RowRights {
  /** Нанять/уволить/вернуть/ссылка, статус, имя/ник/дата найма (canManageRoles). */
  manage: boolean;
  /** Правка телефона: ИО — всем, ОО — не руководителям. */
  edit: boolean;
  /** Роли — только ИО. */
  roles: boolean;
  self: boolean;
  /** Журнал входов (13.10) — только ИО: там IP и сети сотрудников. */
  logs: boolean;
  /** «Не показывать в расстановке» (16.10) — ОО и EO, любому. */
  board: boolean;
  /** «Видит всю историю клиентов» (27.10) — ОО и EO. */
  history: boolean;
}

interface Props {
  row: StaffRow;
  rights: RowRights;
  busy: boolean;
  err: string;
  invite: Invite | null;
  inviteBusy: boolean;
  onStatus: (s: "active" | "vacation") => void;
  onRole: (r: StaffRoleId) => void;
  onPhone: (phone: string) => void;
  onProfile: (patch: ProfilePatch) => void;
  onBoardHidden: (hidden: boolean) => void;
  onHistoryAll: (on: boolean) => void;
  onInvite: () => void;
  onRebind: () => void;
  onFire: () => void;
  onPoll: () => void;
}

const Pen = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M4 20h4L19 9l-4-4L4 16zM13 7l4 4" /></svg>
);

/** Роли, которым вся история клиентов видна и так (API: utils/game1cSeen.ts ROLES_SEE_1C). */
const SEES_ALL: readonly string[] = ["dc", "oo", "eo"];

/** Сегодня по Оренбургу (UTC+5): дата найма не позже — сервер проверяет то же (staff/profile.ts). */
const todayOren = () => new Date(Date.now() + 5 * 3_600_000).toISOString().slice(0, 10);

export function StaffRowDetail(p: Props) {
  const { row, rights } = p;
  const [pad, setPad] = useState<HTMLElement | null>(null);
  const [logOpen, setLogOpen] = useState(false);
  const [text, setText] = useState<"name" | "nick" | null>(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const active = row.status === "active";
  const pen = (label: string, onClick: () => void) => (rights.manage ? (
    <button type="button" className={css.icoBtn} aria-label={label} disabled={p.busy} onClick={onClick}><Pen /></button>
  ) : null);
  const commitText = (v: string) => {
    const t = v.trim();
    if (text === "name") {
      if (!t) setMsg("Имя не сохранено: оно не может быть пустым");
      else if (t !== row.name) { setMsg(null); p.onProfile({ name: t }); }
    } else if (t !== (row.nickname ?? "")) { setMsg(null); p.onProfile({ nickname: t }); }
  };
  const commitDate = (iso: string) => {
    setDateOpen(false);
    if (!iso || iso === (row.hiredAt ?? "").slice(0, 10)) return;
    if (iso > todayOren()) { setMsg(`Дата найма не сохранена: ${fmtDay(iso, true)} — ещё в будущем`); return; }
    if (iso < HIRED_MIN) { setMsg(`Дата найма не сохранена: раньше ${HIRED_MIN_YEAR} года нельзя`); return; }
    setMsg(null);
    p.onProfile({ hiredAt: iso });
  };
  return (
    <div className={css.detail}>
      <div className={css.grid}>
        <div className={css.cell}>
          <span className={css.lbl}>Имя</span>
          <span className={css.val}><span className={css.txt}>{row.name}</span>{pen("Изменить имя", () => setText("name"))}</span>
        </div>
        <div className={css.cell}>
          <span className={css.lbl}>Ник</span>
          <span className={css.val}>
            {row.nickname ? <span className={css.txt}>@{row.nickname}</span> : <span className={css.valMuted}>нет</span>}
            {pen(row.nickname ? "Изменить ник" : "Добавить ник", () => setText("nick"))}
          </span>
        </div>
        <div className={css.cell}>
          <span className={css.lbl}>Телефон</span>
          <span className={css.val}>
            {row.phone
              ? <button type="button" className={css.tel} title="Открыть чат в Telegram" onClick={() => openTgChatByPhone(row.phone ?? "")}>{row.phone}</button>
              : <span className={css.valMuted}>не указан</span>}
            {rights.edit ? (
              <button type="button" className={css.icoBtn} aria-label={row.phone ? "Изменить телефон" : "Добавить телефон"} disabled={p.busy}
                onClick={(e) => setPad(e.currentTarget)}><Pen /></button>
            ) : null}
          </span>
        </div>
        <div className={css.cell}>
          <span className={css.lbl}>Нанят</span>
          <span className={css.val}>
            {fmtDay(row.hiredAt, true) || <span className={css.valMuted}>не указана</span>}
            {pen("Изменить дату найма", () => setDateOpen(true))}
          </span>
        </div>
        <div className={css.cell}>
          <span className={css.lbl}>Telegram</span>
          <span className={`${css.val} ${css.valWrap}`}>
            {row.hasTg ? <span className={css.ok}>привязан</span> : <span className={css.valMuted}>нет входа</span>}
            {rights.manage && !row.hasTg && !p.invite ? (
              <button type="button" className={`${css.mini} ${css.miniGo}`} disabled={p.inviteBusy} onClick={p.onInvite}>{p.inviteBusy ? "…" : "Ссылка"}</button>
            ) : null}
            {rights.manage && row.hasTg && !rights.self ? (
              <button type="button" className={css.mini} disabled={p.inviteBusy} onClick={p.onRebind} title="Потерял телефон: отвязать и выдать новую ссылку">Сменить</button>
            ) : null}
            {rights.logs ? <button type="button" className={css.mini} onClick={() => setLogOpen(true)} title="Кто, когда и откуда входил (с 13.10)">Входы</button> : null}
            {rights.logs ? <AccessLogSheet open={logOpen} mode="staff" staffId={row.id} name={row.name} onClose={() => setLogOpen(false)} /> : null}
          </span>
        </div>
        <div className={css.cell}>
          <span className={css.lbl}>Статус</span>
          <span className={css.val}>
            {rights.manage ? (
              <Switch checked={active} disabled={p.busy} accent="var(--green)" ariaLabel="Работает или в отпуске"
                label={active ? "работает" : "отпуск"} onChange={(on) => p.onStatus(on ? "active" : "vacation")} />
            ) : <span>{active ? "работает" : "в отпуске"}</span>}
          </span>
        </div>
        {rights.board ? (
          <div className={css.cell}>
            <span className={css.lbl}>Расстановка</span>
            <span className={css.val}>
              <Switch checked={!row.boardHidden} disabled={p.busy} accent="var(--green)" ariaLabel="Показывать в дропдаунах расстановки"
                label={row.boardHidden ? "не показывать" : "показывать"} onChange={(on) => p.onBoardHidden(!on)} />
            </span>
          </div>
        ) : null}
        {rights.history ? (
          <div className={`${css.cell} ${css.cellWide}`}>
            <span className={css.lbl}>История клиентов</span>
            <span className={css.val}>
              {row.roles.some((r) => SEES_ALL.includes(r)) ? <span className={css.valMuted}>видит всю — по роли</span> : (
                <Switch checked={row.historyAll} disabled={p.busy} accent="var(--green)" ariaLabel="Видит всю историю клиентов"
                  label={row.historyAll ? "видит всю" : "без чужого архива"} onChange={p.onHistoryAll} />
              )}
            </span>
            <span className={css.note}>Чужие игры 1С (комментарий — без контактов), архив Квестгида и суммы игр в чужих командах — в карточке клиента. Контактов и комментариев чужих команд флаг не открывает</span>
          </div>
        ) : null}
      </div>
      {rights.roles ? (
        <div className={css.cell}>
          <span className={css.lbl}>Роли</span>
          <RoleChips roles={row.roles} onToggle={p.onRole} />
        </div>
      ) : null}
      {p.invite ? <InviteCard invite={p.invite} name={row.name} bound={row.hasTg} busy={p.inviteBusy} onNew={p.onInvite} onPoll={p.onPoll} /> : null}
      {p.err ? <div className={css.err}>{p.err}</div> : null}
      {msg ? <div className={css.err}>{msg}</div> : null}
      {rights.manage && !rights.self ? (
        <div className={css.acts}>
          <button type="button" className={`${ap.abtn} ${ap.dg} ${ap.solo}`} onClick={p.onFire}>Уволить</button>
        </div>
      ) : !rights.manage ? (
        <div className={css.note}>Имя, статус, вход и увольнение этого сотрудника меняет EO.</div>
      ) : null}
      <PhonePad open={!!pad} anchor={pad} label="Телефон" value={row.phone ?? ""}
        onCommit={(v) => {
          const t = v.trim();
          if (t === (row.phone ?? "") || phoneState(t) === "empty") { setMsg(null); return; }
          if (phoneState(t) === "ok") { setMsg(null); p.onPhone(t); } else setMsg(`Телефон не сохранён: номер «${t}» неполный`);
        }}
        onClose={() => setPad(null)} />
      <TextPad open={text !== null} label={text === "nick" ? "Ник" : "Имя и фамилия"} value={text === "nick" ? row.nickname ?? "" : row.name}
        maxLength={text === "nick" ? 40 : 80} placeholder={text === "nick" ? "Как подписать на доске" : "Имя Фамилия"}
        onCommit={commitText} onClose={() => setText(null)} />
      <BirthPicker open={dateOpen} label="Дата найма" value={(row.hiredAt ?? "").slice(0, 10)} fromYear={HIRED_MIN_YEAR} clearable={false} ageNote={false}
        onCommit={commitDate} onCancel={() => setDateOpen(false)} />
    </div>
  );
}
